<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { NButton, NSpin } from 'naive-ui'
import ChannelMessage from './ChannelMessage.vue'
import type { AgentChannelMessage } from '../../stores/agent_channels'
import { useNow } from '../../composables/useNow'

const props = defineProps<{
  messages: AgentChannelMessage[]
  loading: boolean
  error: string | null
  sessionNames?: Record<string, string>
}>()
const emit = defineEmits<{ retry: [] }>()

const GROUP_WINDOW_MS = 5 * 60_000
const STICK_TO_BOTTOM_PX = 80

const now = useNow()
const scroller = ref<HTMLElement | null>(null)

const dayKey = (iso: string) => new Date(iso).toDateString()
const dayLabel = (iso: string) =>
  new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })

const rows = computed(() =>
  props.messages.map((message, i) => {
    const prev = props.messages[i - 1]
    const sameDay = prev && dayKey(prev.created_at) === dayKey(message.created_at)
    const grouped =
      sameDay &&
      prev.sender_name === message.sender_name &&
      prev.source === message.source &&
      new Date(message.created_at).getTime() - new Date(prev.created_at).getTime() < GROUP_WINDOW_MS
    return { message, showHeader: !grouped, day: sameDay ? null : dayLabel(message.created_at) }
  }),
)

function nearBottom(el: HTMLElement): boolean {
  return el.scrollHeight - el.scrollTop - el.clientHeight < STICK_TO_BOTTOM_PX
}

watch(
  () => props.messages.length,
  async () => {
    now.value = new Date()
    const el = scroller.value
    const stick = !el || nearBottom(el)
    await nextTick()
    if (el && stick) el.scrollTop = el.scrollHeight
  },
  { flush: 'pre' },
)
</script>

<template>
  <div ref="scroller" class="flex-1 min-h-0 overflow-y-auto" data-testid="channel-feed">
    <div v-if="loading" class="flex justify-center py-8"><NSpin /></div>
    <div v-else-if="error" class="flex items-center gap-2 text-sm text-red-300" role="alert">
      <span data-testid="channels-load-error">{{ error }}</span>
      <NButton size="tiny" data-testid="channels-retry" @click="emit('retry')">Retry</NButton>
    </div>
    <p v-else-if="messages.length === 0" class="text-sm text-white/60 m-0 py-6 text-center" data-testid="feed-empty">
      No messages yet. Post below, or wait for a member thread to write here.
    </p>
    <ol v-else class="list-none m-0 p-0 flex flex-col gap-2" role="log" aria-live="polite" aria-label="Messages">
      <template v-for="row in rows" :key="row.message.id">
        <li v-if="row.day" class="self-center text-xs text-white/50 py-1" data-testid="day-divider">{{ row.day }}</li>
        <ChannelMessage :message="row.message" :show-header="row.showHeader" :now="now" :session-names="sessionNames" />
      </template>
    </ol>
  </div>
</template>
